import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Airplane, Check, Info, Bed } from '@phosphor-icons/react';

const API = '/api';

const hotelOptions = [
  { value: 'cardedeu', label: 'Cardedeu Hotel (Wedding Venue)' },
  { value: 'buganvillas', label: 'Hotel Buganvillas by Las Palmeras' },
  { value: 'equinoccio', label: 'Equinoccio Hotel' },
  { value: 'airbnb', label: 'Airbnb / Vacation Rental' },
  { value: 'other', label: 'Other' },
  { value: 'undecided', label: 'Not yet decided' },
];

export default function FlightsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [needsTransportation, setNeedsTransportation] = useState(false);
  const [accommodationType, setAccommodationType] = useState('');
  
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const guestId = `guest-${Date.now()}`;
      const payload = {
        guest_id: guestId,
        guest_name: data.guest_name,
        arrival_airline: data.airline,
        arrival_flight: data.flight_number,
        arrival_date: data.arrival_date,
        arrival_time: data.arrival_time,
        departure_airline: data.departure_airline,
        departure_flight: data.departure_flight_number,
        departure_date: data.departure_date,
        departure_time: data.departure_time,
        airport: data.airport || 'SAL',
        needs_transportation: needsTransportation,
        notes: data.notes || null,
        // Accommodation info
        accommodation_type: accommodationType || null,
        accommodation_name: accommodationType === 'other' ? data.accommodation_other : 
                          hotelOptions.find(h => h.value === accommodationType)?.label || null,
        accommodation_checkin: data.accommodation_checkin || null,
        accommodation_checkout: data.accommodation_checkout || null,
        accommodation_notes: data.accommodation_notes || null,
      };

      await axios.post(`${API}/flights`, payload);
      setIsSubmitted(true);
      toast.success('Travel information submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="flight-success" className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#F0EBE3] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-[#B8A88A]" />
          </div>
          <h1 className="font-display text-3xl text-stone-900 mb-4">
            Travel Info Received!
          </h1>
          <p className="font-body text-stone-600 mb-2">
            Thank you for sharing your flight and accommodation details.
          </p>
          <p className="font-body text-sm italic text-stone-400 mb-8">
            ¡Gracias por compartir los detalles de tu vuelo y hospedaje!
          </p>
          <a
            href="/"
            className="inline-block bg-[#C9B99A] text-white px-8 py-3 font-body font-medium
                       transition-all duration-300 hover:bg-[#B8A88A]"
          >
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="flights-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: "#B8A88A" }}>
            Share Your Travel Plans
          </p>
          <p className="font-body text-xs italic text-stone-400 -mt-2 mb-4">
            Comparte tus Planes de Viaje
          </p>
          <h1 className="page-title">Flights & Accommodations</h1>
          <p className="page-subtitle">
            Help us coordinate airport transfers and transportation
          </p>
          <p className="font-body text-sm italic text-stone-400 mt-2">
            Ayúdanos a coordinar traslados del aeropuerto y transporte
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-[#F5F2ED] border-b border-[#E8E2D9]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-start gap-3">
            <Info size={20} style={{ color: "#B8A88A" }} className=" mt-0.5 flex-shrink-0" />
            <div className="font-body text-sm text-stone-700">
              <p className="font-medium mb-1">Flying to El Salvador</p>
              <p>The nearest international airport is <strong>Monseñor Óscar Arnulfo Romero International Airport (SAL)</strong> in San Salvador, approximately 1.5 hours from Lake Coatapeque.</p>
              <p className="text-xs italic text-stone-500 mt-1">El aeropuerto internacional más cercano es SAL en San Salvador, aproximadamente 1.5 horas del Lago de Coatepeque.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="py-16 md:py-24">
        <div className="max-w-xl mx-auto px-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Guest Name */}
            <div className="space-y-2">
              <Label htmlFor="guest_name" className="font-body text-sm text-stone-700">
                Your Full Name / Tu Nombre Completo *
              </Label>
              <Input
                id="guest_name"
                data-testid="input-guest-name"
                {...register('guest_name', { required: 'Name is required' })}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                placeholder="As it appears on your passport"
              />
              {errors.guest_name && (
                <p className="text-red-500 text-sm">{errors.guest_name.message}</p>
              )}
            </div>

            {/* Arrival Section */}
            <div className="p-6 bg-white border border-stone-200 space-y-6">
              <h3 className="font-display text-lg text-stone-900 flex items-center gap-2">
                <Airplane size={20} className="text-[#B8A88A]" />
                Arrival Flight / Vuelo de Llegada
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="airline" className="font-body text-sm text-stone-700">
                    Airline / Aerolínea *
                  </Label>
                  <Input
                    id="airline"
                    data-testid="input-airline"
                    {...register('airline', { required: 'Airline is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="e.g., United, American"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="flight_number" className="font-body text-sm text-stone-700">
                    Flight Number / Número de Vuelo *
                  </Label>
                  <Input
                    id="flight_number"
                    data-testid="input-flight-number"
                    {...register('flight_number', { required: 'Flight number is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="e.g., UA 1234"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="arrival_date" className="font-body text-sm text-stone-700">
                    Arrival Date / Fecha de Llegada *
                  </Label>
                  <Input
                    id="arrival_date"
                    type="date"
                    data-testid="input-arrival-date"
                    {...register('arrival_date', { required: 'Arrival date is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="arrival_time" className="font-body text-sm text-stone-700">
                    Arrival Time / Hora de Llegada *
                  </Label>
                  <Input
                    id="arrival_time"
                    type="time"
                    data-testid="input-arrival-time"
                    {...register('arrival_time', { required: 'Arrival time is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>
              </div>
            </div>

            {/* Departure Section */}
            <div className="p-6 bg-white border border-stone-200 space-y-6">
              <h3 className="font-display text-lg text-stone-900 flex items-center gap-2">
                <Airplane size={20} className="text-[#B8A88A] rotate-90" />
                Departure Flight / Vuelo de Salida
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="departure_airline" className="font-body text-sm text-stone-700">
                    Airline / Aerolínea *
                  </Label>
                  <Input
                    id="departure_airline"
                    data-testid="input-departure-airline"
                    {...register('departure_airline', { required: 'Departure airline is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="e.g., United, American"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure_flight_number" className="font-body text-sm text-stone-700">
                    Flight Number / Número de Vuelo *
                  </Label>
                  <Input
                    id="departure_flight_number"
                    data-testid="input-departure-flight-number"
                    {...register('departure_flight_number', { required: 'Departure flight number is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="e.g., UA 5678"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="departure_date" className="font-body text-sm text-stone-700">
                    Departure Date / Fecha de Salida *
                  </Label>
                  <Input
                    id="departure_date"
                    type="date"
                    data-testid="input-departure-date"
                    {...register('departure_date', { required: 'Departure date is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departure_time" className="font-body text-sm text-stone-700">
                    Departure Time / Hora de Salida *
                  </Label>
                  <Input
                    id="departure_time"
                    type="time"
                    data-testid="input-departure-time"
                    {...register('departure_time', { required: 'Departure time is required' })}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>
              </div>
            </div>

            {/* Accommodation Section */}
            <div className="p-6 bg-white border border-[#C9B99A] space-y-6">
              <h3 className="font-display text-lg text-stone-900 flex items-center gap-2">
                <Bed size={20} className="text-[#B8A88A]" />
                Accommodations / Hospedaje
              </h3>
              <p className="font-body text-sm text-stone-500 -mt-4">
                This helps us coordinate transportation between hotels and the venue.
              </p>
              <p className="font-body text-xs italic text-stone-400 -mt-2">
                Esto nos ayuda a coordinar el transporte entre hoteles y el lugar de la boda.
              </p>

              <div className="space-y-2">
                <Label className="font-body text-sm text-stone-700">
                  Where are you staying? / ¿Dónde te hospedarás?
                </Label>
                <Select value={accommodationType} onValueChange={setAccommodationType}>
                  <SelectTrigger 
                    data-testid="select-accommodation"
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  >
                    <SelectValue placeholder="Select accommodation / Selecciona hospedaje" />
                  </SelectTrigger>
                  <SelectContent>
                    {hotelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {accommodationType === 'other' && (
                <div className="space-y-2">
                  <Label htmlFor="accommodation_other" className="font-body text-sm text-stone-700">
                    Please specify / Por favor especifica
                  </Label>
                  <Input
                    id="accommodation_other"
                    data-testid="input-accommodation-other"
                    {...register('accommodation_other')}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                    placeholder="Hotel name or address"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="accommodation_checkin" className="font-body text-sm text-stone-700">
                    Check-in Date / Fecha de Entrada
                  </Label>
                  <Input
                    id="accommodation_checkin"
                    type="date"
                    data-testid="input-checkin"
                    {...register('accommodation_checkin')}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accommodation_checkout" className="font-body text-sm text-stone-700">
                    Check-out Date / Fecha de Salida
                  </Label>
                  <Input
                    id="accommodation_checkout"
                    type="date"
                    data-testid="input-checkout"
                    {...register('accommodation_checkout')}
                    className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accommodation_notes" className="font-body text-sm text-stone-700">
                  Accommodation Notes (Optional) / Notas de Hospedaje
                </Label>
                <Textarea
                  id="accommodation_notes"
                  data-testid="input-accommodation-notes"
                  {...register('accommodation_notes')}
                  className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A] min-h-[80px]"
                  placeholder="Room sharing, special requests..."
                />
              </div>
            </div>

            {/* Transportation */}
            <div className="p-6 bg-white border border-stone-200 space-y-4">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="needs_transportation"
                  data-testid="checkbox-transportation"
                  checked={needsTransportation}
                  onCheckedChange={(checked) => setNeedsTransportation(checked)}
                />
                <Label htmlFor="needs_transportation" className="font-body cursor-pointer">
                  I would like help arranging transportation from the airport
                </Label>
              </div>
              <p className="font-body text-sm text-stone-500">
                We'll coordinate shuttles for guests arriving around similar times.
              </p>
              <p className="font-body text-xs italic text-stone-400">
                Coordinaremos transporte para invitados que lleguen a horas similares.
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-body text-sm text-stone-700">
                Additional Notes (Optional) / Notas Adicionales
              </Label>
              <Textarea
                id="notes"
                data-testid="input-notes"
                {...register('notes')}
                className="bg-white border-stone-200 focus:border-[#C9B99A] focus:ring-[#C9B99A] min-h-[100px]"
                placeholder="Connecting flights, traveling with others, special needs..."
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              data-testid="submit-flight-btn"
              disabled={isSubmitting}
              className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6 font-body font-medium tracking-wide"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Airplane size={20} weight="fill" />
                  Submit Travel Details
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
